import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { Lock, PanelsTopLeft } from 'lucide-react';
import * as React from 'react';

import { StatusChip } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IPageBlock, ISitePage } from '../contracts';
import { BodyEditor } from '../rich-text';
import { ImagePicker } from '../library';
import {
  ContentMockStrip,
  HistoryPanel,
  SeoFields,
  StatusPill
} from '../shared';
import { seoGaps, useContent } from '../store';
import { ContentPage } from './content-page';

/**
 * The site's standing pages (WEB-12, WEB-15).
 *
 * **A page is a list of blocks its template allows, and nothing else.** WEB-12 is a boundary, not
 * a feature: the editor changes text, images and content items and cannot touch the layout, the
 * template or the structure. Drawing a page as its named blocks — "Hero statement", "Body",
 * "Diagram" — is what makes that boundary obvious rather than enforced by a rule nobody reads. A
 * general page builder would be the opposite: more surface to maintain, and the brand work the
 * agency is producing left open to be broken by accident.
 *
 * **The template is shown and not editable**, with the way to get a new one said out loud (AC2:
 * through the repository, by Marketing Wiz). A screen that silently offers no template control
 * invites the question every week; a screen that answers it once does not.
 *
 * **The launch set is marked.** WEB-03 names five pages for 15 October, and the site's navigation
 * already reaches twenty-two. Which five are the launch set is a fact the content team needs on
 * the screen where they work, not in a plan — so each row carries it.
 */
export function ContentSitePagesPage() {
  const { state } = useContent();
  const [open, setOpen] = React.useState<Nullable<string>>(null);

  const launch = state.pages.filter((page) => page.inLaunchSet);
  const rest = state.pages.filter((page) => !page.inLaunchSet);

  return (
    <ContentPage
      title="Pages"
      lede="The standing pages of the site, and the text each template lets you change."
      strip={<ContentMockStrip needs={['content-store', 'pipeline']} />}
    >
      <Alert>
        <PanelsTopLeft className="h-4 w-4" aria-hidden />
        <AlertTitle>Text and images, not layout</AlertTitle>
        <AlertDescription>
          Each page offers the blocks its template exposes. A new page type or a new layout is
          built by Marketing Wiz through the site repository, not here.
        </AlertDescription>
      </Alert>

      <section className="grid gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          October launch set ({launch.length})
        </h2>
        {launch.map((page) => (
          <PageRow
            key={page.id}
            page={page}
            open={open === page.id}
            onToggle={() => setOpen(open === page.id ? null : page.id)}
          />
        ))}
      </section>

      <section className="grid gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">Other pages ({rest.length})</h2>
        {rest.map((page) => (
          <PageRow
            key={page.id}
            page={page}
            open={open === page.id}
            onToggle={() => setOpen(open === page.id ? null : page.id)}
          />
        ))}
      </section>
    </ContentPage>
  );
}

function PageRow({
  page,
  open,
  onToggle
}: {
  page: ISitePage;
  open: boolean;
  onToggle: () => void;
}) {
  const gaps = seoGaps(page.seo);
  return (
    <div className="grid">
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-md border px-3 py-2',
          open
            ? 'rounded-b-none border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/50 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
            : 'border-border'
        )}
      >
        <div className="grid min-w-0 flex-1 gap-0.5">
          <span className="truncate font-medium text-[var(--color-royal-blue)]">{page.name}</span>
          <span className="truncate text-xs text-muted-foreground">{page.path}</span>
        </div>
        <StatusChip tone="neutral">
          <Lock className="h-3 w-3" aria-hidden />
          {page.template}
        </StatusChip>
        {gaps.length > 0 && <StatusChip tone="attention">Metadata incomplete</StatusChip>}
        <StatusPill status={page.status} />
        <Button variant="outline" size="sm" onClick={onToggle}>
          {open ? 'Close' : 'Edit'}
        </Button>
      </div>
      {open && <PageEditor key={page.id} page={page} onClose={onToggle} />}
    </div>
  );
}

/** Seeded once per page by the caller's `key`, as every editor in this domain is. */
function PageEditor({ page, onClose }: { page: ISitePage; onClose: () => void }) {
  const { send, viewer } = useContent();
  // survey-api's own `content.publish`, as the post editor reads it. See `team-page.tsx`.
  const canPublish = useCan()('content.publish');
  const [blocks, setBlocks] = React.useState(page.blocks);
  const [seo, setSeo] = React.useState(page.seo);
  const [base] = React.useState(page.revision);
  const stale = page.revision !== base;

  const setBlock = (id: string, patch: Partial<IPageBlock>) =>
    setBlocks((current) =>
      current.map((block) => (block.id === id ? { ...block, ...patch } : block))
    );

  return (
    <div className="grid gap-5 rounded-md rounded-t-none border border-t-0 border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/20 p-4 shadow-[inset_3px_0_0_var(--color-royal-blue)]">
      <div className="grid gap-4">
        {blocks.map((block) => (
          <div key={block.id} className="grid gap-1.5">
            {block.kind === 'text' && (
              <>
                <Label htmlFor={`${page.id}-${block.id}`}>{block.label}</Label>
                <Input
                  id={`${page.id}-${block.id}`}
                  value={block.value}
                  onChange={(e) => setBlock(block.id, { value: e.target.value })}
                />
              </>
            )}
            {block.kind === 'rich' && (
              <BodyEditor
                label={block.label}
                value={block.value}
                onChange={(value) => setBlock(block.id, { value })}
              />
            )}
            {block.kind === 'image' && (
              <div className="grid gap-2">
                <ImagePicker
                  label={block.label}
                  value={block.mediaId ?? null}
                  onPick={(mediaId) => setBlock(block.id, { mediaId })}
                />
                <div className="grid gap-1.5">
                  <Label htmlFor={`${page.id}-${block.id}-alt`}>Caption</Label>
                  <Textarea
                    id={`${page.id}-${block.id}-alt`}
                    rows={2}
                    value={block.value}
                    onChange={(e) => setBlock(block.id, { value: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <section className="grid gap-2">
        <h3 className="text-sm font-medium">How this looks when shared</h3>
        <SeoFields
          seo={seo}
          onChange={setSeo}
          pageTitle={`${page.name} | Veritant`}
          socialImageId={seo.socialImageId}
          onPickSocialImage={(socialImageId) => setSeo({ ...seo, socialImageId })}
        />
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">History</CardTitle>
        </CardHeader>
        <CardContent>
          <HistoryPanel
            history={page.history}
            onRevert={(versionNo) =>
              send({ kind: 'revert', of: 'page', id: page.id, versionNo, by: viewer.name })
            }
          />
        </CardContent>
      </Card>

      {stale && (
        <p className="text-xs text-[var(--color-deep-magenta)]">
          {page.updatedBy} changed this page while it was open. Close and reopen to get their
          version.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={stale}
          onClick={() => {
            send({ kind: 'page-save', write: { id: page.id, baseRevision: base, blocks, seo } });
            onClose();
          }}
        >
          Save
        </Button>
        {canPublish && page.status !== 'published' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              send({ kind: 'status', of: 'page', id: page.id, to: 'published', by: viewer.name })
            }
          >
            Publish
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          Last change {formatDateTime(page.updatedAt)} by {page.updatedBy}
        </span>
      </div>
    </div>
  );
}
